// `?eyetest=1` — the real eye system with no character, blueprint or auth around it.

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import EyeTestScene from './EyeTestScene.jsx'
import {
  EYE_SIDES, createEyeController, eyeShellsFromMesh, loadEyeAssetsFromUrls,
} from '../ogs/eyes/index.ts'
import { quaternion } from '../ogs/math/index.ts'
import {
  applyGazeRequest, eyeTestState, eyeTestVersion, subscribeEyeTest, setEyeTest, setGaze,
  resetEyeTest,
} from './eyeTestStore.js'

const ASSET_DIRECTORY = '/assets/ogs/myra'
const ASSET_URLS = {
  meshGlb: `${ASSET_DIRECTORY}/head_eyes.glb`,
  meshJson: `${ASSET_DIRECTORY}/head_eyes.json`,
  leftBin: `${ASSET_DIRECTORY}/eye_static_left.bin`,
  leftJson: `${ASSET_DIRECTORY}/eye_static_left.json`,
  rightBin: `${ASSET_DIRECTORY}/eye_static_right.bin`,
  rightJson: `${ASSET_DIRECTORY}/eye_static_right.json`,
  meta: `${ASSET_DIRECTORY}/eye_meta.json`,
  trunk: `${ASSET_DIRECTORY}/trunk_eye.onnx`,
  geom: `${ASSET_DIRECTORY}/geom_eye.onnx`,
  appr: `${ASSET_DIRECTORY}/appr_eye.onnx`,
}

// Framed on the eye pair, not the head: this scene is judged at eyeball scale.
const LOOK_AT = [0, 1.3777, 0.09]
const CAMERA_POSITION = [0.06, 1.386, 0.42]

// `?head=1` adds the bundle's head branch as Gaussians beside the eyes (docs/OGS_EYES.md).
const headBranchBase = () => (
  new URLSearchParams(window.location.search).get('head') === '1' ? ASSET_DIRECTORY : null)

const panel = {
  position: 'absolute', top: 12, left: 12, zIndex: 10, width: 330,
  padding: '12px 14px', borderRadius: 10, color: 'var(--m-fg)',
  background: 'var(--m-panel)', border: '1px solid var(--m-line)',
  font: '12px/1.5 var(--m-mono)',
}
const row = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }
const label = { width: 76, opacity: 0.75 }

function Slider({ text, value, min, max, onChange }) {
  return (
    <div style={row}>
      <span style={label}>{text}</span>
      <input type="range" min={min} max={max} step={0.5} value={value} style={{ flex: 1 }}
        onChange={(event) => onChange(parseFloat(event.target.value))} />
      <span style={{ width: 44, textAlign: 'right' }}>{value.toFixed(1)}°</span>
    </div>
  )
}

function Toggle({ text, checked, onChange }) {
  return (
    <label style={{ ...row, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{text}</span>
    </label>
  )
}

export default function EyeTestApp() {
  const [assets, setAssets] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState(null)
  const [systemStatus, setSystemStatus] = useState(null)
  const [headStatus, setHeadStatus] = useState(null)
  const [readout, setReadout] = useState(null)
  const systemRef = useRef(null)
  const headRef = useRef(null)
  const readoutRef = useRef(null)
  useSyncExternalStore(subscribeEyeTest, eyeTestVersion)
  const headBase = useMemo(headBranchBase, [])

  // Owned HERE so the debug seam answers even where no WebGPU adapter can build a system.
  const controller = useMemo(
    () => (assets ? createEyeController(eyeShellsFromMesh(assets.mesh)) : null),
    [assets])

  useEffect(() => {
    let cancelled = false
    loadEyeAssetsFromUrls(ASSET_URLS)
      .then((loaded) => {
        if (cancelled) return
        setAssets(loaded)
        setStatus('ready')
        console.info(`[eyetest] loaded head_eyes.glb — head ${loaded.mesh.head.count} verts, `
          + `eyes ${loaded.mesh.eyes.left.count}/${loaded.mesh.eyes.right.count}; chart `
          + `${loaded.charts.left.numTexels}+${loaded.charts.right.numTexels} texels `
          + `@ ${loaded.meta.chartResolution}²`)
      })
      .catch((loadError) => {
        if (cancelled) return
        console.error('[eyetest] load failed', loadError)
        setError(loadError?.message || String(loadError))
        setStatus('error')
      })
    return () => { cancelled = true }
  }, [])

  // A ref, polled on an interval below: the render loop never waits on a setState.
  const onReadout = useCallback((poses) => { readoutRef.current = poses }, [])
  const onSystem = useCallback((next) => {
    systemRef.current = next.system ?? null
    setSystemStatus(next)
  }, [])
  const onHeadStatus = useCallback((next) => {
    headRef.current = next.system ?? null
    setHeadStatus(next)
  }, [])

  useEffect(() => {
    const timer = setInterval(() => setReadout(readoutRef.current), 100)
    return () => clearInterval(timer)
  }, [])

  // The test seam: live getters, gaze resolved on demand through the render loop's own call.
  useEffect(() => {
    const controllerPose = (side) => {
      if (!controller) return null
      applyGazeRequest(controller)
      return controller.updateSide(side)
    }
    window.__EYE_TEST__ = {
      get state() { return status },
      get error() { return error },
      get systemState() { return systemRef.current?.state ?? null },
      // null without ?head=1 — the head branch is not mounted, which is not an error.
      get headState() { return headRef.current?.state ?? null },
      get headError() { return headStatus?.status === 'error' ? headStatus.error : null },
      get counts() {
        if (!assets) return null
        const { mesh } = assets
        return { head: mesh.head.count, left: mesh.eyes.left.count, right: mesh.eyes.right.count }
      },
      get assets() { return assets },
      get lastPass() {
        const state = systemRef.current?.state
        return state ? { passes: state.passes, provider: state.provider, error: state.lastError } : null
      },
      get gazeState() { return JSON.parse(JSON.stringify(eyeTestState)) },
      setGaze: (yawDegrees, pitchDegrees) => {
        setEyeTest({ mode: 'manual', perEye: false })
        setGaze('both', yawDegrees, pitchDegrees)
      },
      setEyeGaze: (side, yawDegrees, pitchDegrees) => {
        setEyeTest({ mode: 'manual', perEye: true })
        setGaze(side, yawDegrees, pitchDegrees)
      },
      setMode: (mode) => setEyeTest({ mode }),
      reset: () => resetEyeTest(),
      quat: (side) => {
        const pose = controllerPose(side)
        return pose ? [...pose.rotation] : null
      },
      axes: (side) => {
        if (!assets) return null
        const shell = assets.mesh.eyes[side]
        const pose = controllerPose(side)
        return {
          pivot: [...shell.pivot],
          meanCentre: [...shell.meanCentre],
          axisOptical: [...shell.axisOptical],
          axisFrame: [...shell.axisFrame],
          poleIndex: shell.poleIndex,
          targetDir: pose ? [...pose.targetDirection] : null,
          frameCentre: pose ? [...pose.frame.centre] : null,
          frameRadius: pose?.frame.radius ?? null,
          aim: pose?.aim ?? null,
          poleMm: pose?.poleTravelMm ?? null,
        }
      },
      verts: (side) => {
        const pose = controllerPose(side)
        return pose ? Float32Array.from(pose.vertices) : null
      },
      restVerts: (side) => {
        if (!assets) return null
        const shell = assets.mesh.eyes[side]
        return quaternion.rotateVertices(shell.local, shell.pivot, quaternion.IDENTITY)
      },
    }
    return () => { delete window.__EYE_TEST__ }
  }, [assets, controller, status, error, systemStatus, headStatus])

  const sliderSides = eyeTestState.perEye ? EYE_SIDES : ['left']
  const manual = eyeTestState.mode === 'manual'
  const systemState = systemStatus?.system?.state ?? null
  const headState = headStatus?.system?.state ?? null

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--m-bg-graph)' }}>
      <div style={panel}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>EYE TEST — head_eyes.glb</div>
        {status === 'loading' && <div style={{ opacity: 0.8 }}>loading…</div>}
        {status === 'error' && (
          <div style={{ color: 'var(--m-danger)', whiteSpace: 'pre-wrap' }}>
            load failed:{'\n'}{error}
          </div>
        )}
        {systemStatus?.status === 'error' && (
          <div style={{ color: 'var(--m-danger)', whiteSpace: 'pre-wrap' }}>
            eye system failed:{'\n'}{systemStatus.error}
          </div>
        )}
        {headStatus?.status === 'error' && (
          <div style={{ color: 'var(--m-danger)', whiteSpace: 'pre-wrap' }}>
            head branch failed:{'\n'}{headStatus.error}
          </div>
        )}
        {status === 'ready' && assets && (
          <>
            <div style={{ opacity: 0.7 }}>
              head {assets.mesh.head.count} · eyes {assets.mesh.eyes.left.count}/{assets.mesh.eyes.right.count} verts
              {systemState ? ` · ${systemState.count} splats · appr ${systemState.provider}` : ' · starting…'}
            </div>
            <div style={{ opacity: 0.7 }}>
              head: {headBase
                ? (headState ? `${headState.count} splats · ${headState.branches
                  .map((branch) => `${branch.name} ${branch.geom}/${branch.appr}`).join(', ')}`
                  : (headStatus?.status === 'error' ? 'branch failed' : 'loading branch…'))
                : 'no gaussians (add ?head=1)'}
              {eyeTestState.show.headWire ? ' · wireframe on' : ' · wireframe off'}
              {headBase && !eyeTestState.show.headSplats && ' · splats hidden'}
            </div>

            <Toggle text="look at camera" checked={eyeTestState.mode === 'camera'}
              onChange={(on) => setEyeTest({ mode: on ? 'camera' : 'manual' })} />
            <Toggle text="per-eye gaze" checked={eyeTestState.perEye}
              onChange={(on) => setEyeTest({ perEye: on })} />

            {manual && sliderSides.map((side) => (
              <div key={side} style={{ marginTop: 4 }}>
                {eyeTestState.perEye && <div style={{ opacity: 0.6 }}>{side}</div>}
                <Slider text="yaw" value={eyeTestState.gaze[side].yawDegrees}
                  min={-eyeTestState.limits.yawDegrees} max={eyeTestState.limits.yawDegrees}
                  onChange={(value) => setGaze(eyeTestState.perEye ? side : 'both',
                    value, eyeTestState.gaze[side].pitchDegrees)} />
                <Slider text="pitch" value={eyeTestState.gaze[side].pitchDegrees}
                  min={-eyeTestState.limits.pitchDegrees} max={eyeTestState.limits.pitchDegrees}
                  onChange={(value) => setGaze(eyeTestState.perEye ? side : 'both',
                    eyeTestState.gaze[side].yawDegrees, value)} />
              </div>
            ))}

            <div style={{ ...row, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={() => resetEyeTest()}>reset</button>
              {[['splats', 'eye splats'], ['eyeMesh', 'eye mesh'], ['headWire', 'head wire'],
                ...(headBase ? [['headSplats', 'head splats']] : []),
                ['eyePoints', 'points'], ['arrows', 'arrows'], ['pivots', 'pivots']]
                .map(([key, text]) => (
                  <button key={key}
                    onClick={() => setEyeTest({ show: { ...eyeTestState.show, [key]: !eyeTestState.show[key] } })}
                    style={{ opacity: eyeTestState.show[key] ? 1 : 0.45 }}>{text}</button>
                ))}
            </div>

            {readout && (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--m-line)', paddingTop: 8, opacity: 0.85 }}>
                {EYE_SIDES.map((side) => (
                  <div key={side} style={{ marginBottom: 4 }}>
                    <div>
                      {side}: aim {readout[side].aim.toFixed(5)} · pole {readout[side].poleTravelMm.toFixed(2)} mm
                    </div>
                    <div style={{ opacity: 0.6 }}>
                      target [{readout[side].targetDirection.map((value) => value.toFixed(3)).join(', ')}]
                    </div>
                  </div>
                ))}
                {systemState && (
                  <div style={{ opacity: 0.6 }}>
                    passes {systemState.passes} · lift {systemState.liftSubmitMs.toFixed(1)}/
                    {systemState.liftReadbackMs.toFixed(1)} ms · lights {systemState.lights.length} ({systemState.lightSource})
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Canvas camera={{ position: CAMERA_POSITION, fov: 32, near: 0.01, far: 50 }} dpr={[1, 1.5]}>
        {assets && controller && (
          <EyeTestScene assets={assets} controller={controller} headBase={headBase}
            onSystem={onSystem} onHeadStatus={onHeadStatus} onReadout={onReadout} />
        )}
        <OrbitControls target={LOOK_AT} minDistance={0.05} maxDistance={3} makeDefault />
      </Canvas>
    </div>
  )
}
